import { ref, computed } from 'vue'
import defaultLogo from '@/assets/isbet-logo.png'
import { useAdminDB } from './useAdminDB'

const SETTING_KEY  = 'custom_logo'
const STORAGE_KEY  = 'isbet_custom_logo'
const MAX_DIMENSION = 320

const customLogo = ref<string | null>(localStorage.getItem(STORAGE_KEY))
const logoLoaded = ref(false)

/** Redimensiona a imagem (canvas) para manter o payload leve antes de persistir. */
function resizeImageToDataUrl(file: File, maxDim = MAX_DIMENSION): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Falha ao ler o arquivo.'))
    reader.onload = () => {
      const img = new Image()
      img.onerror = () => reject(new Error('Arquivo de imagem inválido.'))
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height))
        const w = Math.round(img.width * scale)
        const h = Math.round(img.height * scale)
        const canvas = document.createElement('canvas')
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')
        if (!ctx) { reject(new Error('Canvas não suportado.')); return }
        ctx.drawImage(img, 0, 0, w, h)
        resolve(canvas.toDataURL('image/png'))
      }
      img.src = reader.result as string
    }
    reader.readAsDataURL(file)
  })
}

export function useLogo() {
  const db = useAdminDB()

  const logoUrl       = computed(() => customLogo.value || defaultLogo)
  const hasCustomLogo = computed(() => !!customLogo.value)

  /** Busca a logo persistida (MySQL via n8n); popular uma vez basta — o estado é compartilhado. */
  async function loadLogo(): Promise<void> {
    if (logoLoaded.value) return
    logoLoaded.value = true
    try {
      const settings = await db.loadSettings()
      if (settings[SETTING_KEY]) {
        customLogo.value = settings[SETTING_KEY]
        localStorage.setItem(STORAGE_KEY, settings[SETTING_KEY])
      }
    } catch { /* mantém o cache local (ou o padrão) em caso de falha */ }
  }

  async function setLogoFromFile(file: File): Promise<void> {
    const dataUrl = await resizeImageToDataUrl(file)
    customLogo.value = dataUrl
    localStorage.setItem(STORAGE_KEY, dataUrl)
    await db.updateSetting(SETTING_KEY, dataUrl)
  }

  async function resetLogo(): Promise<void> {
    customLogo.value = null
    localStorage.removeItem(STORAGE_KEY)
    await db.updateSetting(SETTING_KEY, '')
  }

  return { logoUrl, defaultLogo, hasCustomLogo, loadLogo, setLogoFromFile, resetLogo }
}
